export const diseaseModule = {
  id: "disease",
  title: "Thyroid Diseases: Part 1",
  sections: [
    {
      type: "text",
      title: "Graves' Disease — Clinical Depth",
      content: `
        <p><span class="glossary-term">Graves' disease</span> is the most common cause of hyperthyroidism. It is an <strong>autoimmune</strong> condition in which <span class="purple-term">TSH receptor-stimulating antibodies (TRAb)</span> bind to and activate TSH receptors on follicular cells, causing autonomous hormone overproduction.</p>

        <div class="highlight-box">
          <p><strong>Epidemiology & associations</strong></p>
          <ul>
            <li>Female:male ratio ~7:1; peak incidence 20–40 years</li>
            <li>HLA-DR3 and HLA-B8 associations</li>
            <li>Associated with other autoimmune diseases: type 1 DM, Addison's disease, myasthenia gravis, pernicious anaemia</li>
            <li><strong>Smoking</strong> is the most important modifiable risk factor for Graves' orbitopathy</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Graves' disease-specific features (not seen in other causes of hyperthyroidism)</strong></p>
          <ul>
            <li><strong>Graves' orbitopathy (ophthalmopathy)</strong>: TRAb acts on orbital fibroblasts → glycosaminoglycan deposition + T-cell infiltration → periorbital oedema, proptosis (exophthalmos), lid lag, lid retraction, chemosis, corneal exposure, diplopia (EOM involvement)</li>
            <li><strong>Pretibial myxoedema (dermopathy)</strong>: non-pitting thickening of the skin over the shins; orange-peel texture; glycosaminoglycan accumulation in dermis</li>
            <li><strong>Thyroid acropachy</strong>: rare; subperiosteal bone formation causing finger clubbing and swelling</li>
            <li><strong>Thyroid bruit</strong>: markedly increased blood flow to a hyperactive gland</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Graves' orbitopathy management</strong></p>
          <ul>
            <li>Mild: lubricating eye drops, selenium supplementation (6 months — reduces progression)</li>
            <li>Moderate-severe active: IV methylprednisolone pulses (1st line); orbital radiotherapy (if steroids fail)</li>
            <li>Sight-threatening (corneal exposure or optic nerve compression): emergency orbital decompression surgery</li>
            <li>Inactive orbitopathy: rehabilitative surgery (lid, eye muscle, decompression)</li>
            <li>Radioactive iodine <span class="purple-term">can worsen orbitopathy</span> — use steroids cover or avoid in active eye disease</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment of Graves' hyperthyroidism</strong></p>
          <ul>
            <li><strong>Antithyroid drugs (ATDs)</strong>: carbimazole or PTU; two regimens:
              <ul>
                <li><em>Titration</em>: adjust dose to maintain euthyroidism (12–18 months)</li>
                <li><em>Block-replace</em>: high-dose ATD + levothyroxine simultaneously (lower relapse rate in some; more side effects)</li>
              </ul>
            </li>
            <li>Remission rate ~50% after 12–18 months; relapse common (especially if large goitre, high TRAb, male)</li>
            <li><strong>Radioactive iodine (I-131)</strong>: ablates the gland; eventual hypothyroidism in most; avoid in active orbitopathy/pregnancy</li>
            <li><strong>Thyroidectomy</strong>: preferred in large goitres, compression, suspected malignancy, active orbitopathy, patient preference; must render euthyroid first with ATDs</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Graves' Unique Triad</div>
          <div class="mnemonic-content"><strong>"Eyes, Shins, Fingers"</strong> — Graves' orbitopathy (eyes), pretibial myxoedema (shins), and thyroid acropachy (fingers) are unique to Graves' disease and are caused by TRAb, NOT by excess thyroid hormone.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with Graves' disease has worsening proptosis and diplopia. She is a smoker. Radioactive iodine (RAI) is being considered for her hyperthyroidism. What is the key concern with RAI in this patient?",
      options: [
        "RAI causes permanent hypothyroidism within weeks, worsening the orbitopathy",
        "RAI can worsen Graves' orbitopathy, especially in smokers — steroids cover or an alternative should be used",
        "RAI is contraindicated in all women of childbearing age",
        "RAI requires a euthyroid state first, which cannot be achieved in smokers",
      ],
      correct: 1,
      explanation:
        "Radioactive iodine can worsen Graves' orbitopathy (ophthalmopathy), particularly in smokers and those with active eye disease. The mechanism involves a post-RAI release of thyroid antigens and inflammatory cytokines that exacerbate orbital inflammation. Options include: using prophylactic oral corticosteroids alongside RAI, choosing antithyroid drugs as a bridge to surgery, or performing thyroidectomy instead. Smoking is an independent risk factor for development and worsening of Graves' orbitopathy.",
    },
    {
      type: "question",
      text: "What is the mechanism of pretibial myxoedema in Graves' disease, and why can it persist even after the patient becomes hypothyroid following treatment?",
      options: [
        "Excess thyroid hormone stimulates collagen deposition in the skin, which reverses with treatment",
        "TRAb stimulates dermal fibroblasts to accumulate glycosaminoglycans — this is antibody-mediated and independent of thyroid hormone levels",
        "TSH directly stimulates skin fibroblasts when TSH levels are high post-treatment",
        "Hypothyroidism after treatment causes excess mucopolysaccharide deposition in the skin",
      ],
      correct: 1,
      explanation:
        "Pretibial myxoedema is caused by TRAb (TSH receptor antibodies) acting directly on fibroblasts in the dermis — the same mechanism as orbitopathy. TSH receptors are expressed on fibroblasts in the orbit and pretibial skin. TRAb stimulates these fibroblasts to produce excess glycosaminoglycans, causing non-pitting, orange-peel thickening of the skin over the shins. Because it is antibody-mediated (not thyroid hormone-mediated), it can persist or even develop after successful treatment of hyperthyroidism.",
    },
    {
      type: "text",
      title: "Hashimoto's Thyroiditis — Clinical Depth",
      content: `
        <p><span class="glossary-term">Hashimoto's thyroiditis</span> (autoimmune/chronic lymphocytic thyroiditis) is the most common cause of hypothyroidism in iodine-sufficient countries. It represents autoimmune destruction of thyroid follicular cells.</p>

        <div class="highlight-box">
          <p><strong>Pathogenesis</strong></p>
          <ul>
            <li>CD8⁺ cytotoxic T cells cause direct follicular cell apoptosis</li>
            <li>Th1-driven inflammatory response with lymphocytic infiltration</li>
            <li><strong>Anti-TPO antibodies</strong> (~95% of patients) — most sensitive marker</li>
            <li><strong>Anti-thyroglobulin antibodies</strong> (~60–80%)</li>
            <li>These antibodies are disease markers — complement-mediated follicular cell destruction contributes</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Clinical course</strong></p>
          <ul>
            <li>Most patients are initially <strong>euthyroid</strong> with a goitre (firm, rubbery, non-tender)</li>
            <li>Progressive loss of follicular cells → subclinical → overt <strong>hypothyroidism</strong></li>
            <li><strong>Hashitoxicosis</strong> (~5%): transient hyperthyroid phase early in disease — stored hormone released from damaged follicles; low RAI uptake (distinguishes from Graves'); self-limiting</li>
            <li>Histology: lymphocytic infiltration with germinal centre formation, Hürthle cell change (eosinophilic transformation of follicular cells)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Associations</strong></p>
          <ul>
            <li>Other autoimmune diseases: <span class="purple-term">type 1 DM, Addison's disease, coeliac disease, pernicious anaemia, vitiligo, SLE</span></li>
            <li>Autoimmune polyendocrine syndromes (APS): APS type 2 = Hashimoto's + Addison's ± T1DM</li>
            <li>Higher prevalence in: Down syndrome, Turner syndrome, Klinefelter syndrome</li>
            <li>HLA-DR3, HLA-DR5</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Complications</strong></p>
          <ul>
            <li>Hypothyroidism (eventual in most)</li>
            <li>Goitre (may cause local compressive symptoms)</li>
            <li><span class="purple-term">Primary thyroid lymphoma</span>: rare but important; most are MALT lymphoma or diffuse large B-cell lymphoma; occurs in elderly women with chronic Hashimoto's; rapidly enlarging painless goitre; diagnose with core biopsy; treat with chemotherapy ± radiotherapy</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: Levothyroxine when TSH is elevated; target TSH 0.5–2.5 mIU/L. Selenium supplementation may reduce anti-TPO titres and improve quality of life.</p>
      `,
    },
    {
      type: "question",
      text: "A patient with known Hashimoto's thyroiditis develops a rapidly enlarging painless thyroid mass over 4 weeks with dysphagia. TSH is elevated. What is the most important diagnosis to exclude and how?",
      options: [
        "Anaplastic thyroid carcinoma — core biopsy; FNAC may be non-diagnostic",
        "Primary thyroid lymphoma — core biopsy (FNAC insufficient to classify lymphoma); treat with chemo ± radiotherapy",
        "Papillary thyroid carcinoma — FNAC showing Orphan Annie nuclei",
        "Hashitoxicosis — low RAI uptake, self-limiting",
      ],
      correct: 1,
      explanation:
        "In a patient with chronic Hashimoto's thyroiditis who develops a rapidly enlarging thyroid mass, primary thyroid lymphoma must be excluded. It usually presents in elderly women with long-standing Hashimoto's. The lymphoma most often arises from the MALT (mucosa-associated lymphoid tissue) built up in the autoimmune thyroiditis. Core biopsy (not FNAC) is needed to classify the lymphoma subtype. FNAC cannot reliably distinguish lymphoma subtypes. Treatment is chemotherapy (R-CHOP) ± radiotherapy, not surgery.",
    },
    {
      type: "question",
      text: "A 35-year-old woman with Hashimoto's thyroiditis presents with palpitations and mild tremor 2 months after diagnosis. TSH is suppressed, fT4 is elevated, and RAI uptake is very low. What is happening?",
      options: [
        "She has developed concurrent Graves' disease with high RAI uptake",
        "Hashitoxicosis — transient hyperthyroidism from release of stored hormone from damaged follicles; low RAI uptake confirms this is not Graves'",
        "She is over-replaced with levothyroxine",
        "She has a toxic adenoma developing within the Hashimoto's thyroid",
      ],
      correct: 1,
      explanation:
        "Hashitoxicosis occurs in ~5% of Hashimoto's patients, usually early in the disease. Autoimmune-mediated follicular destruction releases pre-formed thyroid hormone into the circulation, causing transient thyrotoxicosis. The key differentiator from Graves' disease is the very low or absent RAI uptake — in Graves', uptake is high or normal (active synthesis). Hashitoxicosis is self-limiting; treatment is symptomatic (beta-blocker). Antithyroid drugs are not needed as there is no active hormone synthesis.",
    },
    {
      type: "text",
      title: "Thyroiditis: De Quervain's, Postpartum & Riedel's",
      content: `
        <p>Several forms of thyroiditis can cause transient thyroid dysfunction. Understanding the distinguishing features is key.</p>

        <div class="highlight-box">
          <p><strong>Subacute (de Quervain's) thyroiditis — post-viral</strong></p>
          <ul>
            <li>Causes: Coxsackie virus, mumps, EBV, influenza</li>
            <li>Features: <span class="purple-term">painful, tender thyroid</span>, fever, malaise, preceded by URTI</li>
            <li>Labs: ↑ESR, ↑CRP, low RAI uptake (destroyed follicles cannot trap iodine)</li>
            <li>Triphasic course: thyrotoxicosis (4–8 weeks) → hypothyroid (4–12 weeks) → recovery</li>
            <li>Treatment: NSAIDs (mild) or prednisolone (severe); beta-blocker for thyrotoxic symptoms; levothyroxine if hypothyroid phase prolonged</li>
            <li>~95% fully recover; ~5% develop permanent hypothyroidism</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Postpartum thyroiditis</strong></p>
          <ul>
            <li>Occurs in 5–10% of women; typically 1–6 months post delivery</li>
            <li>Mechanism: rebound of immune system after pregnancy-related immunosuppression; anti-TPO antibodies positive</li>
            <li>Same triphasic course: thyrotoxicosis (1–3 months) → hypothyroid (4–6 months) → recovery (majority)</li>
            <li><strong>~25% develop permanent hypothyroidism</strong> (especially if anti-TPO positive)</li>
            <li>Treatment: beta-blocker (thyrotoxic phase); levothyroxine (hypothyroid phase); breastfeeding compatible</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Riedel's thyroiditis</strong></p>
          <ul>
            <li>Rare; dense <strong>fibrous replacement</strong> of thyroid parenchyma extending into surrounding structures</li>
            <li>Clinically mimics thyroid carcinoma: very hard, "woody" thyroid, fixed to trachea/oesophagus</li>
            <li>Can cause dysphagia, dyspnoea, hoarseness (RLN compression)</li>
            <li>Associated with <span class="purple-term">IgG4-related disease</span> (retroperitoneal fibrosis, sclerosing cholangitis, autoimmune pancreatitis)</li>
            <li>Treatment: steroids (may halt progression) + tamoxifen (anti-fibrotic); surgery to relieve compression; hypothyroidism treated with levothyroxine</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Distinguishing Thyroiditis Types</div>
          <div class="mnemonic-content"><strong>De Quervain's</strong> = PAIN + ↑ESR + post-viral<br/><strong>Postpartum</strong> = painLESS + post-delivery + anti-TPO+<br/><strong>Hashimoto's</strong> = painLESS + chronic + hypothyroid<br/><strong>Riedel's</strong> = HARD + fibrosis + IgG4-related</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A woman 10 weeks postpartum presents with palpitations and anxiety. On examination her thyroid is non-tender. TSH is suppressed. Anti-TPO antibodies are positive. RAI uptake is low. What is the diagnosis and what is the expected course?",
      options: [
        "Graves' disease — high RAI uptake expected; treat with antithyroid drugs",
        "Postpartum thyroiditis — low RAI uptake; expect a triphasic course with thyrotoxicosis followed by hypothyroid phase; ~25% develop permanent hypothyroidism",
        "De Quervain's thyroiditis — high ESR and painful thyroid expected",
        "Toxic adenoma — a hot nodule on scan would confirm",
      ],
      correct: 1,
      explanation:
        "Postpartum thyroiditis classically presents with a painless thyroid, suppressed TSH, and low RAI uptake (unlike Graves' which has high uptake). Anti-TPO antibodies positive. It follows a triphasic course: thyrotoxicosis (1–3 months post delivery) → hypothyroid phase (4–6 months) → recovery. However, ~25% develop permanent hypothyroidism and need long-term levothyroxine. The thyrotoxic phase is managed symptomatically with beta-blockers; antithyroid drugs are ineffective (no active synthesis).",
    },
    {
      type: "question",
      text: "A 45-year-old woman presents with a very hard, fixed thyroid mass causing mild dysphagia. Ultrasound suggests the mass extends behind the trachea. Core biopsy shows dense fibrous tissue replacing thyroid parenchyma with no malignant cells. What is the diagnosis?",
      options: [
        "Anaplastic thyroid carcinoma — biopsy may be falsely negative; repeat urgently",
        "Riedel's thyroiditis — dense fibrous replacement of the thyroid associated with IgG4-related disease",
        "Hashimoto's thyroiditis — large goitre with central fibrosis",
        "Subacute de Quervain's thyroiditis — look for tenderness",
      ],
      correct: 1,
      explanation:
        "Riedel's thyroiditis is characterised by dense, non-malignant fibrous tissue replacing the thyroid and invading surrounding structures, causing a rock-hard, fixed thyroid. It is associated with IgG4-related disease (multifocal fibrosclerosis). The key diagnostic challenge is distinguishing it from anaplastic carcinoma — core biopsy is essential. Treatment includes corticosteroids (may slow fibrosis) and tamoxifen (anti-fibrotic properties). Surgery may be needed to relieve tracheal or oesophageal compression.",
    },
    {
      type: "text",
      title: "Toxic Multinodular Goitre & Toxic Adenoma",
      content: `
        <p>Not all hyperthyroidism is due to Graves' disease. These conditions produce thyroid hormone autonomously and are more common in older patients and iodine-deficient areas.</p>

        <div class="highlight-box">
          <p><strong>Toxic multinodular goitre (MNG) — Plummer's disease</strong></p>
          <ul>
            <li>Most common cause of hyperthyroidism in <strong>elderly patients</strong> and in iodine-deficient areas</li>
            <li>Multiple autonomous nodules produce T3/T4 independently of TSH</li>
            <li>Typically TSH mutations in TSH receptor or Gsα → constitutive activation</li>
            <li>Scintigraphy: patchy, heterogeneous (areas of hot + suppressed tissue)</li>
            <li>Insidious onset; may present with atrial fibrillation or unexplained weight loss in elderly</li>
            <li>Treatment: RAI preferred for most (effective, non-invasive); thyroidectomy for large/compressive goitres; antithyroid drugs for short-term control</li>
            <li><strong>Unlikely to remit with ATDs alone</strong> — unlike Graves' disease, no immune remission is expected</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Toxic adenoma (Plummer's adenoma)</strong></p>
          <ul>
            <li>Single autonomous nodule with a TSH receptor or Gsα mutation → constitutive activation</li>
            <li>TSH suppressed; rest of thyroid suppressed on scintigraphy (<span class="purple-term">"hot nodule"</span> with surrounding cold tissue)</li>
            <li>Low malignancy risk (&lt;1% of hot nodules are malignant)</li>
            <li>Treatment: RAI (if &lt;3 cm) or surgery; watch and wait with beta-blocker if subclinical and small</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>RAI uptake scan — differentiating causes of hyperthyroidism</strong></p>
          <ul>
            <li><strong>High/diffuse uptake</strong>: Graves' disease</li>
            <li><strong>Patchy uptake</strong>: toxic MNG</li>
            <li><strong>Focal hot nodule, rest suppressed</strong>: toxic adenoma</li>
            <li><strong>Very low/absent uptake</strong>: thyroiditis (de Quervain's, postpartum), exogenous thyroid hormone, iodine load</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 72-year-old man with a long-standing multinodular goitre develops new-onset atrial fibrillation. TSH is suppressed and fT4 is mildly elevated. Scintigraphy shows patchy uptake. What is the diagnosis?",
      options: [
        "Graves' disease — most common cause of hyperthyroidism",
        "Toxic multinodular goitre — the most common cause of hyperthyroidism in elderly patients; patchy scintigraphy scan",
        "Toxic adenoma — single hot nodule on scan",
        "De Quervain's thyroiditis — low uptake expected; tender thyroid",
      ],
      correct: 1,
      explanation:
        "Toxic multinodular goitre (Plummer's disease) is the most common cause of hyperthyroidism in elderly patients, especially in iodine-deficient areas. The patchy scintigraphy confirms multiple autonomously functioning nodules. AF is a common complication of hyperthyroidism in elderly patients (less likely to have tachycardia and tremor than younger patients). ATDs do not induce remission — definitive treatment is RAI or thyroidectomy.",
    },
    {
      type: "text",
      title: "Thyroid Storm & Myxoedema Coma",
      content: `
        <p>These are life-threatening thyroid emergencies requiring prompt recognition and aggressive management.</p>

        <div class="highlight-box">
          <p><strong>Thyroid storm (thyrotoxic crisis)</strong></p>
          <ul>
            <li>Precipitants: thyroid/non-thyroid surgery, infection, MI, trauma, childbirth, contrast dye, RAI treatment</li>
            <li>Features: high fever (&gt;40°C), tachycardia/AF, agitation/confusion/psychosis, diarrhoea/vomiting, jaundice (hepatic failure)</li>
            <li><strong>Burch-Wartofsky Point Scale (BWPS)</strong>: score &gt;45 = likely thyroid storm; 25–44 = impending</li>
          </ul>
          <div class="mnemonic-card">
            <div class="mnemonic-title">Thyroid Storm Treatment — "5 Bs"</div>
            <div class="mnemonic-content">
              <strong>B</strong>lock synthesis — PTU 600 mg loading dose then 250 mg every 4 hours (or carbimazole)<br/>
              <strong>B</strong>lock release — Lugol's iodide <em>1 hour AFTER PTU</em> (Wolff-Chaikoff; giving iodide first would fuel more synthesis)<br/>
              <strong>B</strong>eta-blocker — propranolol IV (heart rate, tremor, blocks T4→T3 conversion)<br/>
              <strong>B</strong>lock conversion — hydrocortisone 100 mg IV q8h (blocks T4→T3, replaces relative adrenal insufficiency)<br/>
              <strong>B</strong>ackground — treat the precipitant; cooling; supportive care; cholestyramine (binds T3/T4 in gut)
            </div>
          </div>
        </div>

        <div class="highlight-box">
          <p><strong>Myxoedema coma (decompensated hypothyroidism)</strong></p>
          <ul>
            <li>Precipitants: cold exposure, infection, drugs (opioids, sedatives, anaesthetics), surgery</li>
            <li>Features: <strong>hypothermia</strong>, bradycardia, hypoventilation/respiratory failure, hypotension, altered consciousness (stupor → coma), hyponatraemia, hypoglycaemia, non-pitting oedema</li>
            <li>Labs: low fT4, high TSH; ↑CK, ↑cholesterol; hyponatraemia</li>
          </ul>
          <div class="mnemonic-card">
            <div class="mnemonic-title">Myxoedema Coma Treatment</div>
            <div class="mnemonic-content">
              <strong>T3 (liothyronine) IV</strong> — faster onset than T4; or high-dose levothyroxine (T4) IV<br/>
              <strong>Hydrocortisone 50–100 mg IV every 6–8 hours</strong> — coexistent adrenal insufficiency must be assumed and treated before thyroid hormone (giving T4 without steroids in adrenal insufficiency → adrenal crisis)<br/>
              <strong>Passive rewarming</strong> — no active external heating (causes vasodilation and cardiovascular collapse)<br/>
              <strong>Respiratory support</strong> — often requires intubation/ventilation<br/>
              <strong>Treat precipitant</strong> — antibiotics if infection; glucose for hypoglycaemia
            </div>
          </div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with known Graves' disease undergoes emergency appendicectomy. 24 hours postoperatively she develops fever of 41°C, heart rate 150/min, confusion, and vomiting. What is the most likely diagnosis and what is the first-line treatment?",
      options: [
        "Sepsis from surgical site infection — broad-spectrum antibiotics",
        "Thyroid storm — PTU, then Lugol's iodide (1 hour later), propranolol, hydrocortisone, and treat the precipitant",
        "Acute adrenal crisis — IV hydrocortisone immediately",
        "Malignant hyperthermia — dantrolene and cooling",
      ],
      correct: 1,
      explanation:
        "Surgery is one of the most common precipitants of thyroid storm in a patient with pre-existing hyperthyroidism. The combination of fever >40°C, tachycardia, confusion, and GI symptoms post-surgery in a Graves' patient is classic. Treatment: PTU first (blocks both synthesis AND T4→T3 conversion), then Lugol's iodide ≥1 hour later (blocks hormone release), propranolol (heart rate + blocks conversion), hydrocortisone (blocks conversion, covers adrenal insufficiency). The order matters — iodide must never be given before ATDs.",
    },
    {
      type: "question",
      text: "A 75-year-old hypothyroid patient is brought to ED in winter, confused and hypothermic (34°C). TSH is very high, fT4 very low. Why must hydrocortisone be given before or alongside thyroid hormone replacement?",
      options: [
        "Hydrocortisone blocks T4 breakdown, prolonging the effect of replacement",
        "Untreated adrenal insufficiency (which commonly coexists) can precipitate an adrenal crisis when thyroid hormone is given, as it accelerates cortisol metabolism",
        "Hydrocortisone reverses hypothermia more quickly than thyroid hormone",
        "Hydrocortisone reduces the risk of cardiac arrhythmia from rapid thyroid hormone replacement",
      ],
      correct: 1,
      explanation:
        "Myxoedema coma frequently coexists with adrenal insufficiency (secondary, from hypothalamic-pituitary disease, or primary in autoimmune polyendocrine syndromes). In hypothyroidism, cortisol metabolism is slowed, masking the insufficiency. When thyroid hormone is given, cortisol metabolism normalises rapidly — but if the adrenal glands cannot respond, an acute adrenal crisis is precipitated. Therefore, hydrocortisone must always be given before or simultaneously with thyroid hormone in myxoedema coma.",
    },
    {
      type: "text",
      title: "Subclinical Thyroid Disease & Sick Euthyroid Syndrome",
      content: `
        <p>These conditions require careful interpretation of thyroid function tests without reflexive treatment.</p>

        <div class="highlight-box">
          <p><strong>Subclinical hypothyroidism</strong> — normal fT4, elevated TSH</p>
          <ul>
            <li><strong>When to treat</strong>:
              <ul>
                <li>TSH &gt;10 mIU/L — treat regardless of symptoms</li>
                <li>TSH 4–10 mIU/L + symptoms present</li>
                <li>Pregnancy or planning pregnancy (TSH should be &lt;2.5 in first trimester)</li>
                <li>Anti-TPO positive (high risk of progression to overt hypothyroidism)</li>
                <li>Younger patients (&lt;65) with cardiovascular risk factors</li>
              </ul>
            </li>
            <li>Observe (repeat TFTs in 3–6 months) if TSH 4–10 and asymptomatic, elderly, anti-TPO negative</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Subclinical hyperthyroidism</strong> — normal fT4/fT3, suppressed TSH</p>
          <ul>
            <li><strong>Always treat if</strong>:
              <ul>
                <li>TSH &lt;0.1 mIU/L AND patient &gt;65 years (AF risk, osteoporosis)</li>
                <li>Known cardiac disease or osteoporosis</li>
                <li>Symptomatic</li>
              </ul>
            </li>
            <li>TSH 0.1–0.4 (mildly suppressed) in &lt;65: observe</li>
            <li>Common cause: excess levothyroxine dose — reduce dose</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Sick euthyroid syndrome (non-thyroidal illness)</strong></p>
          <ul>
            <li>In critical illness, starvation, or major surgery: ↓deiodinase activity → ↓T3 (low T3 syndrome), ↓T4 in severe illness, normal or low TSH</li>
            <li>Mechanisms: ↓type 1 deiodinase → less T4→T3 conversion; ↑reverse T3 (rT3)</li>
            <li>This is a protective response — reduces metabolic demands during illness</li>
            <li><strong>Do NOT treat</strong> — resolves with recovery of underlying illness</li>
            <li>Key differentiator from central hypothyroidism: rT3 elevated in sick euthyroid (not in central hypothyroidism); TFTs normalise as patient recovers</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 72-year-old woman has TSH of 7.5 mIU/L and normal fT4. She is asymptomatic. Anti-TPO antibodies are negative. Should she be treated with levothyroxine?",
      options: [
        "Yes — any TSH above normal requires immediate treatment",
        "Not necessarily — with TSH 4–10, asymptomatic presentation, negative anti-TPO, and elderly age, observation with repeat TFTs is appropriate",
        "Yes — elderly patients have a higher risk of cardiac complications from untreated hypothyroidism",
        "No — TSH must exceed 20 mIU/L before treating elderly patients",
      ],
      correct: 1,
      explanation:
        "Subclinical hypothyroidism with TSH 4–10 mIU/L does not automatically require treatment. In an elderly asymptomatic patient with negative anti-TPO (lower risk of progression to overt hypothyroidism), the risk-benefit ratio favours observation with repeat TFTs in 3–6 months. Starting levothyroxine in elderly patients risks over-replacement, which can cause AF and accelerate bone loss. Treatment is strongly indicated when TSH >10 mIU/L, in symptomatic patients, in pregnancy, or when anti-TPO is positive.",
    },
    {
      type: "question",
      text: "An ITU patient with severe sepsis has low T3 and T4 with a normal-low TSH. No prior thyroid disease is documented. What is the most likely explanation and what should be done?",
      options: [
        "Central hypothyroidism from pituitary suppression — start IV levothyroxine immediately",
        "Sick euthyroid syndrome (non-thyroidal illness) — do not treat; TFTs will normalise with recovery",
        "Primary hypothyroidism precipitated by sepsis — check anti-TPO and start levothyroxine",
        "Amiodarone-induced hypothyroidism — stop amiodarone",
      ],
      correct: 1,
      explanation:
        "Sick euthyroid syndrome is almost universal in critical illness. Cytokines and reduced caloric intake suppress deiodinase activity, reducing T3 (and T4 in severe illness). TSH is normal or mildly low. Reverse T3 (rT3) is elevated. This is a protective adaptive response that reduces catabolism. Treating with thyroid hormone does not improve outcomes and may be harmful. TFTs should be repeated after recovery from the acute illness before concluding there is underlying thyroid disease.",
    },
  ],
};
